// LOCAL: structure mirrors apps/crew-merchant-ui/src/components/QuickSearch.tsx —
// `Popover` + `Command`, ⌘K event listener, gradient-bordered trigger button
// styled to look like a search bar, `PopoverContent` overlapping the trigger
// via a negative `sideOffset` so the input appears where the trigger sat.
// Trimmed: merchant-ui composes five `useSearchByKind` calls (Return /
// Warranty / Shipping / Registration / Order) hitting `/:storeId/search`.
// The internal admin only has one searchable surface today — the global
// stores list — so this version runs a single `api.stores.list({ searchTerm })`
// and renders results in one `CommandGroup`.
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounceValue, useEventListener } from 'usehooks-ts';
import api from '~/api';

type Store = CommandAdminApi.ResponseBody<'/store', 'get'>[number];
type User = CommandAdminApi.ResponseBody<'/user', 'get'>[number];
function StoreCommandContent({ store }: { store: Store }) {
  return (
    <div className="flex w-full items-center gap-3 px-2">
      <Icon icon="store" className="text-neutral-700" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-neutral-900">
          {store.name}
        </p>
        <span className="text-xs text-neutral-600">ID:{store.id}</span>
      </div>
    </div>
  );
}

function UserCommandContent({ user }: { user: User }) {
  return (
    <div className="flex w-full items-center gap-3 px-2">
      <Icon icon="users" className="text-neutral-700" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-neutral-900">
          {user.email}
        </p>
        {user.firstName && user.lastName && (
          <span className="text-xs text-neutral-600">
            {user.firstName} {user.lastName}
          </span>
        )}
      </div>
    </div>
  );
}

function useStoreCommandSearch(popoverOpen: boolean, trimmed: string) {
  return useQuery({
    queryKey: ['quick-search-stores', trimmed],
    queryFn: () => api.stores.list({ searchTerm: trimmed }),
    enabled: popoverOpen && trimmed.length > 0,
  });
}

function useUserCommandSearch(popoverOpen: boolean, trimmed: string) {
  return useQuery({
    queryKey: ['quick-search-users', trimmed],
    queryFn: () => api.users.search(trimmed),
    enabled: popoverOpen && trimmed.length > 0,
  });
}

export default function CommandSearch() {
  const trigger = useRef<HTMLButtonElement>(null);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 250);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const navigate = useNavigate();

  // one of the known use cases for the deprecated `platform` property
  const isApplePlatform = useMemo(
    () => /Mac|iPod|iPhone|iPad/.test(navigator.platform),
    [],
  );

  useEventListener('keydown', (event) => {
    if (event.key !== 'k') return;
    if (!(event.metaKey || event.ctrlKey)) return;
    event.preventDefault();
    setPopoverOpen(true);
  });

  const trimmed = debouncedSearch.trim();
  const storesQuery = useStoreCommandSearch(popoverOpen, trimmed);
  const usersQuery = useUserCommandSearch(popoverOpen, trimmed);

  const stores = storesQuery.data ?? [];
  const users = usersQuery.data ?? [];
  const isAwaitingResults = storesQuery.isFetching || usersQuery.isFetching;
  const hasResults = stores.length > 0 || users.length > 0;

  const handleSelectStore = (store: Store) => {
    setPopoverOpen(false);
    navigate(`/stores/${store.id}`);
  };

  const handleSelectUser = (user: User) => {
    setPopoverOpen(false);
    navigate(`/users/${user.id}`);
  };

  return (
    // omitting clearing debouncedSearch from onOpenChange allows the previous
    // results to be displayed when the popover reopens
    <Popover modal open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group w-full max-w-xl rounded-xl bg-linear-to-br from-neutral-600 via-neutral-700 to-neutral-600 p-px text-left shadow-md transition-all duration-100 hover:bg-linear-to-bl hover:from-neutral-700 hover:via-neutral-800 hover:to-neutral-700"
          ref={trigger}
        >
          <div className="flex w-full items-center rounded-xl bg-neutral-800 px-3 py-1.5 text-sm text-white/50 transition-all duration-100 group-hover:bg-neutral-950">
            <div className="flex w-full items-center justify-between">
              <div>
                <FontAwesomeIcon icon={faMagnifyingGlass} className="mr-2" />
                Search for stores or users..
              </div>
              <kbd className="flex items-center justify-center rounded-md border border-neutral-600 px-1 py-0.5 font-sans text-xs">
                {isApplePlatform ? '⌘' : 'Ctrl'}&nbsp;K
              </kbd>
            </div>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        // overlap the content over the trigger
        sideOffset={-1 * (trigger.current?.offsetHeight ?? 0)}
      >
        {/* no need to filter, will instead show all results returned by API */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Quick Search"
            className="w-full"
            onValueChange={setDebouncedSearch}
          />

          <CommandList>
            {!isAwaitingResults && !hasResults && (
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 p-4">
                  <Icon icon="search" className="h-10 w-10 text-neutral-500" />
                  <p className="text-sm text-neutral-500">
                    {trimmed
                      ? `No matches found for “${trimmed}”.`
                      : 'Find users or stores.'}
                  </p>
                </div>
              </CommandEmpty>
            )}

            {isAwaitingResults && (
              <CommandLoading>
                <p className="p-4 text-center text-sm text-neutral-500">
                  Searching…
                </p>
              </CommandLoading>
            )}

            {stores.length > 0 && (
              <CommandGroup heading={<p className="text-xs">Stores</p>}>
                {stores.map((store) => (
                  <CommandItem
                    key={`store-${store.id}`}
                    value={`${store.id} ${store.name} ${store.myShopifyDomain}`}
                    onSelect={() => handleSelectStore(store)}
                  >
                    <StoreCommandContent store={store} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {users.length > 0 && (
              <CommandGroup heading={<p className="text-xs">Users</p>}>
                {users.map((user) => (
                  <CommandItem
                    key={`user-${user.id}`}
                    value={`${user.id} ${user.email}`}
                    onSelect={() => handleSelectUser(user)}
                  >
                    <UserCommandContent user={user} />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
